import React, { useState } from 'react';
import { Calendar, Clock, MapPin, Users, Send } from 'lucide-react';

const Booking = () => {
  const [formData, setFormData] = useState({
    name1: '',
    name2: '',
    email: '',
    phone: '',
    eventDate: '',
    eventTime: '',
    location: '',
    guestCount: '',
    serviceType: '',
    package: '',
    message: '',
    howHeard: '',
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: Connect to backend
    console.log('Booking request:', formData);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center" data-testid="booking-success">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 rounded-full bg-gold-500/20 flex items-center justify-center mx-auto mb-6">
            <Send className="w-10 h-10 text-gold-500" />
          </div>
          <h2 className="text-3xl font-serif text-warm-100 mb-4">
            Request Received!
          </h2>
          <p className="text-warm-100/70 mb-8">
            Thank you for your interest. We'll review your request and be in touch within 24-48 hours to discuss your special day.
          </p>
          <a href="/" className="btn-gold text-sm tracking-wider uppercase">
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="booking-page">
      {/* Hero */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-moody/30 to-brand-charcoal" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gold-500 tracking-[0.3em] uppercase text-sm mb-4">
            Begin Your Journey
          </p>
          <h1 className="text-4xl md:text-5xl font-serif text-warm-100 mb-6">
            Book Your Ceremony
          </h1>
          <div className="divider-gold max-w-xs mx-auto" />
          <p className="text-warm-100/70 text-lg max-w-2xl mx-auto mt-6">
            Fill out the form below to request a consultation. We'll be in touch to discuss your vision and answer any questions.
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-container">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
          <div className="card-elegant p-8 md:p-12">
            {/* Couple Names */}
            <div className="mb-8">
              <h3 className="text-xl font-serif text-warm-100 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-gold-500" />
                The Couple
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-warm-100/70 text-sm mb-2">Partner 1 Name *</label>
                  <input
                    type="text"
                    name="name1"
                    value={formData.name1}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                    data-testid="input-name1"
                  />
                </div>
                <div>
                  <label className="block text-warm-100/70 text-sm mb-2">Partner 2 Name *</label>
                  <input
                    type="text"
                    name="name2"
                    value={formData.name2}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                    data-testid="input-name2"
                  />
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="mb-8">
              <h3 className="text-xl font-serif text-warm-100 mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-warm-100/70 text-sm mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="block text-warm-100/70 text-sm mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div className="mb-8">
              <h3 className="text-xl font-serif text-warm-100 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gold-500" />
                Event Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-warm-100/70 text-sm mb-2">Event Date *</label>
                  <input
                    type="date"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                    data-testid="input-date"
                  />
                </div>
                <div>
                  <label className="block text-warm-100/70 text-sm mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Event Time
                  </label>
                  <input
                    type="time"
                    name="eventTime"
                    value={formData.eventTime}
                    onChange={handleChange}
                    className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                    data-testid="input-time"
                  />
                </div>
                <div>
                  <label className="block text-warm-100/70 text-sm mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Venue/Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="City, State or Venue Name"
                    className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 placeholder-warm-100/30 focus:outline-none focus:border-gold-500 transition-colors"
                    data-testid="input-location"
                  />
                </div>
                <div>
                  <label className="block text-warm-100/70 text-sm mb-2">Expected Guest Count</label>
                  <select
                    name="guestCount"
                    value={formData.guestCount}
                    onChange={handleChange}
                    className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                    data-testid="input-guests"
                  >
                    <option value="">Select...</option>
                    <option value="1-10">Just us (Elopement)</option>
                    <option value="11-30">Intimate (11-30)</option>
                    <option value="31-75">Medium (31-75)</option>
                    <option value="76-150">Large (76-150)</option>
                    <option value="150+">Grand (150+)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Service Selection */}
            <div className="mb-8">
              <h3 className="text-xl font-serif text-warm-100 mb-4">Service Interest</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-warm-100/70 text-sm mb-2">Ceremony Type *</label>
                  <select
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                    data-testid="input-service"
                  >
                    <option value="">Select a service...</option>
                    <option value="wedding">Wedding Ceremony</option>
                    <option value="vow-renewal">Vow Renewal</option>
                    <option value="commitment">Commitment Ceremony</option>
                    <option value="elopement">Elopement</option>
                  </select>
                </div>
                <div>
                  <label className="block text-warm-100/70 text-sm mb-2">Package Interest</label>
                  <select
                    name="package"
                    value={formData.package}
                    onChange={handleChange}
                    className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                    data-testid="input-package"
                  >
                    <option value="">Not sure yet</option>
                    <option value="essential">Essential ($350+)</option>
                    <option value="signature">Signature ($550+)</option>
                    <option value="premium">Premium ($850+)</option>
                    <option value="custom">Custom Package</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="mb-8">
              <label className="block text-warm-100/70 text-sm mb-2">Tell Us About Your Vision</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                placeholder="Share details about your ceremony vision, any special requests, or questions you have..."
                className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 placeholder-warm-100/30 focus:outline-none focus:border-gold-500 transition-colors resize-none"
                data-testid="input-message"
              />
            </div>

            {/* How did you hear */}
            <div className="mb-8">
              <label className="block text-warm-100/70 text-sm mb-2">How did you hear about us?</label>
              <select
                name="howHeard"
                value={formData.howHeard}
                onChange={handleChange}
                className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                data-testid="input-how-heard"
              >
                <option value="">Select...</option>
                <option value="google">Google Search</option>
                <option value="social">Social Media</option>
                <option value="referral">Friend/Family Referral</option>
                <option value="vendor">Vendor Referral</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="btn-gold w-full text-sm tracking-[0.2em] uppercase flex items-center justify-center gap-2"
              data-testid="submit-booking-btn"
            >
              <Send className="w-5 h-5" />
              Submit Booking Request
            </button>

            <p className="text-warm-100/50 text-sm text-center mt-4">
              We typically respond within 24-48 hours.
            </p>
          </div>
        </form>
      </section>
    </div>
  );
};

export default Booking;
